import { useStore, useTranslationStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  getLastStoryLanguage,
  recordLastStoryLanguage,
} from '@src/utility/lastStoryLanguage';
import { useCallback, useEffect, useMemo } from 'react';

import { useReproduceCaptionsOnTranslationEdit } from './useReproduceCaptionsOnTranslationEdit';

import type { Language } from '@src/types.ts/language';

/**
 * Wires a consumer surface into `TranslationStore`:
 *
 *   1. Bridge `VideoCreatorStore.storyId` → stores-v2 `RootStore.storyId`
 *      (TranslationStore and other v2 consumers read storyId off
 *      RootStore; without this bridge the translation flow throws).
 *   2. Register the showcase's supported languages so the toggle knows
 *      which codes are selectable + translatable.
 *   3. Discover any previously-generated translations from S3 so the
 *      toggle surfaces them on mount — without this, prior-session
 *      translations don't appear until regenerate.
 *   4. Populate editorial context (story title + description) so LLM
 *      handlers fold it into their system prompt for register/terminology.
 *
 * Every hydration step is idempotent on the shared store, so calling
 * this hook from multiple surfaces (Creator Studio's LeftSidePanel,
 * Content Studio's Transcript & Speakers tab, etc.) is safe — whichever
 * mounts first warms the cache.
 *
 * Returns the resolved `supportedLanguages` list so callers can pass it
 * to downstream components (e.g. `TranslationToggle`,
 * `TranslationGenerateModal`).
 */
export function useTranslationStoreHydration(): Language[] {
  const rootStore = useStore();
  const translationStore = useTranslationStore();
  const videoCreator = useVideoCreatorStore();

  // PLAT-9685 / PLAT-11557: claim the shared TranslationStore for this story
  // BEFORE bridging the id into RootStore. The store clears itself when the
  // claim moves to a different story; without that, prior-story `translations`
  // keep `discoverTranslations` from fetching the new story's data (it
  // short-circuits on `translations.has(lang)`) and the language dropdown leaks
  // the previous story's translations into the new one.
  //
  // The claim lives in the store rather than in a ref here (PLAT-9685's original
  // shape) because a ref only sees a story change while THIS hook instance stays
  // mounted across it — and none do. Content Studio swaps `ContentView` out for
  // `ContentStudioSkeleton` while the new story loads, unmounting every consumer;
  // each remounts with the new id already in hand and reads its own ref as "first
  // bridge, cache is already empty", so the reset never ran. `setStory` compares
  // against what the CACHE holds, which survives the unmount, and is a no-op when
  // the story is unchanged (so a clip-preview modal opening over the active story
  // doesn't discard its pending edits).
  useEffect(() => {
    const nextStoryId = videoCreator.storyId ?? null;
    translationStore.setStory(nextStoryId);
    rootStore.storyId = nextStoryId;
  }, [rootStore, translationStore, videoCreator.storyId]);

  const supportedLanguages = useMemo(
    () => videoCreator.organization?.supportedLanguages ?? [],
    [videoCreator.organization?.supportedLanguages],
  );
  useEffect(() => {
    translationStore.setAvailableLanguages(
      supportedLanguages.map(l => l.value),
    );
  }, [supportedLanguages, translationStore]);

  useEffect(() => {
    const storyId = videoCreator.storyId ?? null;
    if (storyId === null) return;
    void translationStore.discoverTranslations(storyId);
  }, [videoCreator.storyId, translationStore]);

  // PLAT-11898 — a translation removed from any surface stops being the
  // story's remembered language, so no reopen restores an empty translated
  // view. Every consumer subscribes; clearing an already-cleared memory is a
  // no-op.
  useEffect(() => {
    const storyId = videoCreator.storyId ?? null;
    if (storyId === null) return;
    return translationStore.onLanguageRemoved(removed => {
      if (getLastStoryLanguage(storyId) === removed) {
        recordLastStoryLanguage(storyId, null);
      }
    });
  }, [videoCreator.storyId, translationStore]);

  const storyTitle = videoCreator.story?.title;
  const storyDescription = videoCreator.story?.description;
  useEffect(() => {
    if (!storyTitle && !storyDescription) {
      translationStore.setStoryContext(null);
      return;
    }
    translationStore.setStoryContext({
      ...(storyTitle ? { title: storyTitle } : {}),
      ...(storyDescription ? { summary: storyDescription } : {}),
    });
  }, [storyTitle, storyDescription, translationStore]);

  // Keep the rendered video captions in sync with translation edits. A
  // popover edit updates the store but doesn't re-produce the karaoke track,
  // so without this the baked subtitles keep the stale machine translation
  // until the language is re-toggled. Scoped to the story-level editor; the
  // clip-preview surface wires its own (clip-scoped) editor in
  // ClipTranscriptBridge.
  const refreshStoryKaraoke = useCallback(() => {
    videoCreator.rootStore?.editorStore?.refreshKaraoke();
  }, [videoCreator.rootStore?.editorStore]);
  // PLAT-10945 — the refresh above is display-only, so without a save the
  // rebuilt track never reaches storage and a reload shows the pre-correction
  // captions. `refreshStoryKaraoke` runs first and syncs the global
  // KaraokeStore from the editor, which is what `prepareCurrentVideoForSave`
  // harvests. This is `VideoCreatorStore`'s existing save entry point, the same
  // one every other edit on this surface uses.
  //
  // One deliberate difference from the clip host: this save also moves a clip's
  // review status from "New" to "Working" and clears the lightweight flag (the
  // story-level method does that for every save, automatic ones included).
  //
  // What stops this from also saving the story's video while the user works in
  // Content Studio is the reaction's own two guards — the story editor must
  // hold a caption track AND its default caption language must be a real
  // translation. Measured on a cold load of Content Studio on 2026-07-30: the
  // story editor's default caption language is `original`, so the reaction
  // stops there and only the clip's own reaction saves. That is a measurement
  // of one entry route, not a guarantee: Creator Studio and Content Studio are
  // two client-side routes of one application sharing one editor, so a user who
  // sets a translated caption language in Creator Studio and then navigates to
  // Content Studio in-app arrives with the guards open. That route has not been
  // driven; do not read this comment as saying it cannot happen.
  //
  // PLAT-10945 card 7: five components call this hook, so this callback is
  // built up to five times over one editor. They share one reaction, so one
  // correction still costs one save — see `useReproduceCaptionsOnTranslationEdit`.
  const persistStoryKaraoke = useCallback(() => {
    void videoCreator.saveCurrentAndParentVideos(true, true, true);
  }, [videoCreator]);
  useReproduceCaptionsOnTranslationEdit(
    videoCreator.rootStore?.editorStore?.editor,
    translationStore,
    refreshStoryKaraoke,
    persistStoryKaraoke,
  );

  return supportedLanguages;
}
